import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Plus, Trash, X, UsersThree } from "@phosphor-icons/react";
import { toast } from "sonner";

const rid = (p) => `${p}-${Math.random().toString(36).slice(2)}`;
const emptyProp = () => ({ id: rid("new"), address: "", city: "", state: "", zip: "", notes: "" });
const emptyContact = () => ({ id: rid("new"), name: "", email: "", phone: "", title: "" });
const emptyForm = () => ({ name: "", email: "", phone: "", company: "", notes: "", lead_source: "", tags: [], properties: [], contacts: [] });

// props: open, onClose, editing (client|null), onSaved
export default function ClientDialog({ open, onClose, editing, onSaved }) {
  const [form, setForm] = useState(emptyForm());
  const [tagInput, setTagInput] = useState("");
  const [allTags, setAllTags] = useState([]);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    api.clientTags().then(setAllTags).catch(() => {});
    if (editing) setForm({ ...emptyForm(), ...editing, tags: editing.tags || [], properties: editing.properties || [], contacts: editing.contacts || [] });
    else setForm(emptyForm());
    setTagInput("");
  }, [open, editing]);

  const addTag = (t) => {
    const v = t.trim();
    if (v && !form.tags.includes(v)) setForm((f) => ({ ...f, tags: [...f.tags, v] }));
    setTagInput("");
  };
  const removeTag = (t) => setForm((f) => ({ ...f, tags: f.tags.filter((x) => x !== t) }));
  const setProp = (idx, patch) => setForm((f) => ({ ...f, properties: f.properties.map((p, i) => (i === idx ? { ...p, ...patch } : p)) }));
  const setContact = (idx, patch) => setForm((f) => ({ ...f, contacts: f.contacts.map((c, i) => (i === idx ? { ...c, ...patch } : c)) }));

  const save = async () => {
    if (!form.name.trim()) return toast.error("Client name is required");
    const strip = (arr) => arr.map(({ id, ...rest }) => (id?.startsWith("new-") ? rest : { id, ...rest }));
    const payload = {
      ...form,
      properties: strip(form.properties),
      contacts: strip(form.contacts).filter((c) => (c.name || "").trim() || (c.email || "").trim() || (c.phone || "").trim()),
    };
    setSaving(true);
    try {
      if (editing) await api.updateClient(editing.id, payload);
      else await api.createClient(payload);
      toast.success(`Client ${editing ? "updated" : "added"}`);
      onSaved?.(); onClose();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to save client"); }
    finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="client-dialog">
        <DialogHeader><DialogTitle className="font-display text-xl">{editing ? "Edit" : "New"} Client</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Name *</Label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5" data-testid="client-name-input" /></div>
            <div><Label>Company</Label><Input value={form.company} onChange={(e) => setForm({ ...form, company: e.target.value })} className="mt-1.5" /></div>
            <div><Label>Email</Label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5" data-testid="client-email-input" /></div>
            <div><Label>Phone</Label><Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1.5" /></div>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div><Label>Lead Source</Label><Input value={form.lead_source} onChange={(e) => setForm({ ...form, lead_source: e.target.value })} placeholder="Referral, Google, Facebook…" className="mt-1.5" data-testid="client-lead-source" /></div>
            <div>
              <Label>Tags</Label>
              <input list="client-tags-list" value={tagInput} onChange={(e) => setTagInput(e.target.value)}
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addTag(tagInput); } }}
                onBlur={() => tagInput && addTag(tagInput)}
                placeholder="Type & press Enter" data-testid="client-tag-input"
                className="mt-1.5 flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm" />
              <datalist id="client-tags-list">{allTags.map((t) => <option key={t} value={t} />)}</datalist>
            </div>
          </div>
          {form.tags.length > 0 && (
            <div className="flex flex-wrap gap-1.5" data-testid="client-tags-chips">
              {form.tags.map((t) => (
                <span key={t} className="inline-flex items-center gap-1 text-xs font-semibold bg-cyan-50 text-cyan-700 border border-cyan-100 rounded-full px-2.5 py-1">
                  {t}<button onClick={() => removeTag(t)} className="hover:text-rose-500"><X size={12} weight="bold" /></button>
                </span>
              ))}
            </div>
          )}

          {/* Properties */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label>Properties / Sites</Label>
              <Button variant="ghost" size="sm" onClick={() => setForm((f) => ({ ...f, properties: [...f.properties, emptyProp()] }))} className="text-[#00B4D8]" data-testid="add-property-button"><Plus size={15} className="mr-1" /> Add property</Button>
            </div>
            <div className="space-y-3">
              {form.properties.map((p, idx) => (
                <div key={p.id || idx} className="border border-slate-200 rounded-md p-3 bg-slate-50">
                  <div className="flex gap-2">
                    <Input value={p.address} onChange={(e) => setProp(idx, { address: e.target.value })} placeholder="Street address" data-testid={`property-address-${idx}`} />
                    <button onClick={() => setForm((f) => ({ ...f, properties: f.properties.filter((_, i) => i !== idx) }))} className="text-slate-400 hover:text-rose-500 px-1"><Trash size={16} /></button>
                  </div>
                  <div className="grid grid-cols-3 gap-2 mt-2">
                    <Input value={p.city} onChange={(e) => setProp(idx, { city: e.target.value })} placeholder="City" />
                    <Input value={p.state} onChange={(e) => setProp(idx, { state: e.target.value })} placeholder="State" />
                    <Input value={p.zip} onChange={(e) => setProp(idx, { zip: e.target.value })} placeholder="Zip" />
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Contacts / sub-clients */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <Label className="flex items-center gap-1.5"><UsersThree size={15} weight="duotone" className="text-[#00B4D8]" /> Additional Contacts</Label>
              <Button variant="ghost" size="sm" onClick={() => setForm((f) => ({ ...f, contacts: [...f.contacts, emptyContact()] }))} className="text-[#00B4D8]" data-testid="add-contact-button"><Plus size={15} className="mr-1" /> Add contact</Button>
            </div>
            <p className="text-xs text-slate-400 mb-2">Extra emails or points of contact for a commercial account (billing, site manager, etc.).</p>
            <div className="space-y-3">
              {form.contacts.map((c, idx) => (
                <div key={c.id || idx} className="border border-slate-200 rounded-md p-3 bg-slate-50" data-testid={`contact-row-${idx}`}>
                  <div className="flex gap-2">
                    <Input value={c.name} onChange={(e) => setContact(idx, { name: e.target.value })} placeholder="Contact name" data-testid={`contact-name-${idx}`} />
                    <Input value={c.title} onChange={(e) => setContact(idx, { title: e.target.value })} placeholder="Role (e.g. Billing)" />
                    <button onClick={() => setForm((f) => ({ ...f, contacts: f.contacts.filter((_, i) => i !== idx) }))} className="text-slate-400 hover:text-rose-500 px-1"><Trash size={16} /></button>
                  </div>
                  <div className="grid grid-cols-2 gap-2 mt-2">
                    <Input type="email" value={c.email} onChange={(e) => setContact(idx, { email: e.target.value })} placeholder="Email" data-testid={`contact-email-${idx}`} />
                    <Input value={c.phone} onChange={(e) => setContact(idx, { phone: e.target.value })} placeholder="Phone" />
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div><Label>Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-1.5" /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={save} disabled={saving} className="bg-[#0B1726] hover:bg-slate-800" data-testid="save-client-button">{saving ? "Saving…" : "Save Client"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
